import { isDefined } from "@clipboard-health/util-ts";
import { Box, List } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { type Agent } from "@src/appV2/User/api/useSearchAgents";
import { type Facility } from "@src/appV2/User/api/useSearchFacilities";
import { useEffect, useRef } from "react";

import { type WorkerShift } from "./api/useGetWorkerShifts";
import { type WorkplaceShift } from "./api/useGetWorkplaceShifts";
import { type ShiftSearchResult } from "./api/useSearchShift";
import { AgentResultItem } from "./components/AgentResultItem";
import { FacilityResultItem } from "./components/FacilityResultItem";
import { SectionHeader } from "./components/SectionHeader";
import { type ShiftAction, ShiftActionItem } from "./components/ShiftActionItem";
import { ShiftResultItem } from "./components/ShiftResultItem";
import { WorkerShiftItem } from "./components/WorkerShiftItem";
import { WorkplaceShiftItem } from "./components/WorkplaceShiftItem";
import { type SearchResultItem } from "./hooks/useOmnisearchResults";

interface OmnisearchResultsListProps {
  agents: Agent[];
  facilities: Facility[];
  shift: ShiftSearchResult | null;
  expandedShiftId: string | null;
  expandedWorkerId: string | null;
  expandedWorkplaceId: string | null;
  workerShifts: WorkerShift[];
  isLoadingWorkerShifts: boolean;
  workerHasShifts: boolean;
  workplaceShifts: WorkplaceShift[];
  isLoadingWorkplaceShifts: boolean;
  workplaceHasShifts: boolean;
  selectedIndex: number;
  copiedItem: ShiftAction | "shift" | null;
  onSelect: (item: SearchResultItem) => void;
}

export function OmnisearchResultsList(props: OmnisearchResultsListProps) {
  const {
    agents,
    facilities,
    shift,
    expandedShiftId,
    expandedWorkerId,
    expandedWorkplaceId,
    workerShifts,
    isLoadingWorkerShifts,
    workerHasShifts,
    workplaceShifts,
    isLoadingWorkplaceShifts,
    workplaceHasShifts,
    selectedIndex,
    copiedItem,
    onSelect,
  } = props;
  const isShiftExpanded = isDefined(shift) && expandedShiftId === shift.id;
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());
  useEffect(() => {
    const element = itemRefs.current.get(selectedIndex);
    element?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [selectedIndex]);
  const registerRef = (index: number) => (element: HTMLElement | null) => {
    if (element) {
      itemRefs.current.set(index, element);
    } else {
      itemRefs.current.delete(index);
    }
  };
  const getAgentResultIndex = (agentIndex: number): number => {
    let index = 0;
    for (let agentNumber = 0; agentNumber < agentIndex; agentNumber++) {
      index += 1;
      if (expandedWorkerId === agents[agentNumber].userId) {
        index += workerShifts.length;
      }
    }
    return index;
  };

  const getFacilitiesBaseIndex = (): number =>
    agents.length + (isDefined(expandedWorkerId) ? workerShifts.length : 0);

  const getFacilityResultIndex = (facilityIndex: number): number => {
    let index = getFacilitiesBaseIndex();
    for (let facilityNumber = 0; facilityNumber < facilityIndex; facilityNumber++) {
      index += 1;
      if (expandedWorkplaceId === facilities[facilityNumber].userId) {
        index += workplaceShifts.length;
      }
    }
    return index;
  };

  const getShiftBaseIndex = (): number => {
    let index = getFacilitiesBaseIndex() + facilities.length;
    if (isDefined(expandedWorkplaceId)) {
      index += workplaceShifts.length;
    }
    return index;
  };

  const shiftBaseIndex = getShiftBaseIndex();

  return (
    <List sx={{ py: 0 }}>
      {agents.length > 0 && (
        <>
          <SectionHeader label="Professionals" />
          {agents.map((agent, agentIndex) => {
            const isExpanded = expandedWorkerId === agent.userId;
            const resultIndex = getAgentResultIndex(agentIndex);
            return (
              <Box key={agent.userId}>
                <AgentResultItem
                  ref={registerRef(resultIndex)}
                  agent={agent}
                  selected={selectedIndex === resultIndex}
                  expanded={isExpanded}
                  isLoadingShifts={isLoadingWorkerShifts}
                  onClick={() => {
                    onSelect({ type: "agent", data: agent, expanded: isExpanded });
                  }}
                />
                {isExpanded && !isLoadingWorkerShifts && workerHasShifts && (
                  <>
                    {workerShifts.map((workerShift, shiftIndex) => {
                      const shiftResultIndex = resultIndex + 1 + shiftIndex;
                      return (
                        <WorkerShiftItem
                          key={workerShift.id}
                          ref={registerRef(shiftResultIndex)}
                          shift={workerShift}
                          selected={selectedIndex === shiftResultIndex}
                          copied={copiedItem === "shift" && selectedIndex === shiftResultIndex}
                          onClick={() => {
                            onSelect({
                              type: "agent-shift",
                              data: workerShift,
                              workerId: agent.userId,
                            });
                          }}
                        />
                      );
                    })}
                  </>
                )}
                {isExpanded && !isLoadingWorkerShifts && !workerHasShifts && (
                  <Text color="text.secondary" sx={{ pl: 6, pr: 2, py: 1 }} variant="caption">
                    No recent or upcoming shifts
                  </Text>
                )}
              </Box>
            );
          })}
        </>
      )}
      {facilities.length > 0 && (
        <>
          <SectionHeader hasTopMargin={agents.length > 0} label="Workplaces" />
          {facilities.map((facility, facilityIndex) => {
            const isExpanded = expandedWorkplaceId === facility.userId;
            const resultIndex = getFacilityResultIndex(facilityIndex);
            return (
              <Box key={facility.userId}>
                <FacilityResultItem
                  ref={registerRef(resultIndex)}
                  facility={facility}
                  selected={selectedIndex === resultIndex}
                  expanded={isExpanded}
                  isLoadingShifts={isLoadingWorkplaceShifts}
                  onClick={() => {
                    onSelect({ type: "facility", data: facility, expanded: isExpanded });
                  }}
                />
                {isExpanded && !isLoadingWorkplaceShifts && workplaceHasShifts && (
                  <>
                    {workplaceShifts.map((workplaceShift, shiftIndex) => {
                      const shiftResultIndex = resultIndex + 1 + shiftIndex;
                      return (
                        <WorkplaceShiftItem
                          key={workplaceShift.id}
                          ref={registerRef(shiftResultIndex)}
                          shift={workplaceShift}
                          selected={selectedIndex === shiftResultIndex}
                          copied={copiedItem === "shift" && selectedIndex === shiftResultIndex}
                          onClick={() => {
                            onSelect({
                              type: "workplace-shift",
                              data: workplaceShift,
                              workplaceId: facility.userId,
                            });
                          }}
                        />
                      );
                    })}
                  </>
                )}
                {isExpanded && !isLoadingWorkplaceShifts && !workplaceHasShifts && (
                  <Text color="text.secondary" sx={{ pl: 6, pr: 2, py: 1 }} variant="caption">
                    No recent or upcoming shifts
                  </Text>
                )}
              </Box>
            );
          })}
        </>
      )}
      {isDefined(shift) && (
        <>
          <SectionHeader hasTopMargin={agents.length > 0 || facilities.length > 0} label="Shifts" />
          <ShiftResultItem
            ref={registerRef(shiftBaseIndex)}
            expanded={isShiftExpanded}
            selected={selectedIndex === shiftBaseIndex}
            shift={shift}
            onClick={() => {
              onSelect({ type: "shift", data: shift, expanded: isShiftExpanded });
            }}
          />
          {isShiftExpanded && (
            <>
              {isDefined(shift.agent?.userId) && (
                <ShiftActionItem
                  ref={registerRef(shiftBaseIndex + 1)}
                  action="worker"
                  copied={copiedItem === "worker"}
                  selected={selectedIndex === shiftBaseIndex + 1}
                  shift={shift}
                  onClick={() => {
                    onSelect({ type: "shift-action", data: shift, action: "worker" });
                  }}
                />
              )}
              {isDefined(shift.facility?.userId) && (
                <ShiftActionItem
                  ref={registerRef(shiftBaseIndex + 1 + (isDefined(shift.agent?.userId) ? 1 : 0))}
                  action="workplace"
                  copied={copiedItem === "workplace"}
                  selected={
                    selectedIndex === shiftBaseIndex + 1 + (isDefined(shift.agent?.userId) ? 1 : 0)
                  }
                  shift={shift}
                  onClick={() => {
                    onSelect({ type: "shift-action", data: shift, action: "workplace" });
                  }}
                />
              )}
            </>
          )}
        </>
      )}
    </List>
  );
}
